<template>
  <template v-if="projects">
    <DataTable
      id="projects"
      :wrap-text="false"
      :wrapping-borders="false"
      :enable-column-customization="true"
      :columns="columns"
      :data="projects"
    >
      <template #name="{row: project}: {row: Project}">
        <div>
          <div class="flex gap-2 items-center">
            <console-link
              :to="`/cloud/projects/${project.id}`"
              class="block mb-1 hover:no-underline text-base text-link"
            >
              <strong>{{ project.name }}</strong>
            </console-link>
          </div>
        </div>
      </template>

      <template #status="{row: project}">
        <ProjectStatusBadge
          :status="project.status"
          class="relative -top-0.5"
        />
        <ProjectStatusBadge
          v-if="project.type.handle === ProjectType.Sandbox"
          :status="project.type"
          class="relative -top-0.5"
        />
      </template>

      <template #integration_provider="{row: project}">
        <div class="flex gap-0.5">
          <GitProviderIcon
            :provider="project.integration?.provider"
            class="w-4"
          />
          <code class="text-xs">
            {{ project.repository?.full_name }}
          </code>
        </div>
      </template>

      <template #updated_at="{row: project}: {row: Project}">
        <relative-time :datetime="project.updated_at">
          {{ project.updated_at }}
        </relative-time>
      </template>

      <template #domains_count="{row: project}: {row: Project}">
        <div>
          <console-link :to="`/cloud/projects/${project.id}/domains`">
            <template v-if="project.domains_count">
              {{ project.domains_count }}
              {{ project.domains_count > 1 ? 'domains' : 'domain' }}
            </template>
            <template v-else>No domains</template>
          </console-link>
        </div>
      </template>

      <template #environments="{row: project}: {row: Project}">
        <div>
          <console-link :to="`/cloud/projects/${project.id}/environments`">
            <template v-if="project.environments_count">
              {{ project.environments_count }}
              {{
                project.environments_count > 1 ? 'environments' : 'environment'
              }}
            </template>
            <template v-else>No environments</template>
          </console-link>
        </div>
      </template>

      <template #database_engine="{row}: {row: Project}">
        <Badge>{{ row.cluster?.database_engine?.name }}</Badge>
      </template>
    </DataTable>
  </template>
</template>

<script setup lang="ts">
  import ConsoleLink from '@console/components/ConsoleLink.vue';
  import type {Project} from '@/console';
  import ProjectStatusBadge from '@console/components/cloud/project/ProjectStatusBadge.vue';
  import Badge from '@/common/ui/components/Badge.vue';
  import {ProjectType} from '@/types/ProjectType';
  import {type ColumnDef, createColumnHelper} from '@tanstack/vue-table';
  import {ref} from 'vue';
  import GitProviderIcon from '@console/components/cloud/GitProviderIcon.vue';
  import DataTable from '@console/components/tables/DataTable.vue';

  defineProps<{
    projects: Project[];
  }>();

  const columnHelper = createColumnHelper<Project>();
  const columns = ref<ColumnDef<Project, any>[]>([
    columnHelper.accessor('name', {
      header: 'Name',
    }),
    columnHelper.accessor('status', {
      enableSorting: false,
      header: 'Status',
    }),
    columnHelper.accessor('integration.provider', {
      enableSorting: false,
      header: 'Repository',
    }),
    columnHelper.accessor('database_engine', {
      enableSorting: false,
      header: 'Database',
    }),
    columnHelper.accessor('cluster.region.name', {
      enableSorting: false,
      header: 'Region',
    }),
    columnHelper.accessor('domains_count', {
      enableSorting: false,
      header: 'Domains',
    }),
    columnHelper.accessor('environments', {
      enableSorting: false,
      header: 'Environments',
    }),
    columnHelper.accessor('updated_at', {
      header: 'Updated',
    }),
  ]);
</script>

<style scoped></style>
